import type { OpenStatus, ShopInfo, Weekday } from '../../lib/twin';
import { weekdayName } from '../../lib/twin';
import { OpenStatusBadge } from './OpenStatusBadge';

interface Props {
  shop: ShopInfo;
  status: OpenStatus;
}

function formatHm(value: string): string {
  const [h, m] = value.split(':').map(Number);
  const period = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m.toString().padStart(2, '0')} ${period}`;
}

export function HoursAndLocation({ shop, status }: Props) {
  const today = new Date().getDay() as Weekday;
  const days = [0, 1, 2, 3, 4, 5, 6] as Weekday[];
  const mapsQuery = encodeURIComponent(
    `${shop.address_line}, ${shop.city}, ${shop.region} ${shop.postal_code}`,
  );

  return (
    <section
      id="hours"
      aria-labelledby="hours-heading"
      className="mx-auto max-w-6xl px-5 py-16 sm:py-20"
    >
      <div className="grid gap-10 lg:grid-cols-2">
        <div>
          <h2 id="hours-heading" className="text-3xl font-bold sm:text-4xl">
            Hours &amp; location
          </h2>
          <div className="mt-4">
            <OpenStatusBadge status={status} />
          </div>

          <table className="mt-6 w-full max-w-sm border-collapse text-left">
            <caption className="sr-only">Weekly opening hours</caption>
            <tbody>
              {days.map((day) => {
                const window = shop.hours[day];
                const isToday = day === today;
                return (
                  <tr
                    key={day}
                    className={`border-b border-mocha/10 ${isToday ? 'font-bold text-espresso' : 'text-espresso-soft'}`}
                  >
                    <th scope="row" className="py-2 pr-4 font-medium">
                      {weekdayName(day)}
                      {isToday && (
                        <span className="ml-2 rounded bg-accent-soft px-1.5 py-0.5 text-xs font-semibold text-accent-dark">
                          Today
                        </span>
                      )}
                    </th>
                    <td className="py-2 text-right tabular-nums">
                      {window
                        ? `${formatHm(window.open)} – ${formatHm(window.close)}`
                        : 'Closed'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div>
          <address className="not-italic">
            <p className="text-xl font-semibold">{shop.name}</p>
            <p className="mt-1 text-espresso-soft">
              {shop.address_line}
              <br />
              {shop.neighborhood}, {shop.city}, {shop.region} {shop.postal_code}
            </p>
          </address>

          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <a
              href={`tel:${shop.phone}`}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-cream-50 px-5 py-2.5 font-semibold text-espresso ring-2 ring-inset ring-mocha/30 hover:bg-cream-100"
            >
              <span aria-hidden="true">📞</span> Call {shop.phone}
            </a>
            <a
              href={`https://maps.google.com/?q=${mapsQuery}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-cream-50 px-5 py-2.5 font-semibold text-espresso ring-2 ring-inset ring-mocha/30 hover:bg-cream-100"
            >
              <span aria-hidden="true">🧭</span> Get directions
            </a>
          </div>

          {/* Static map placeholder — decorative. */}
          <div
            aria-hidden="true"
            className="mt-5 flex aspect-[16/9] items-center justify-center rounded-[var(--radius-card)] bg-gradient-to-br from-foam to-cream-200 ring-1 ring-mocha/15"
          >
            <span className="text-espresso-soft">🗺️ map placeholder</span>
          </div>
        </div>
      </div>
    </section>
  );
}
